import { t } from '@/i18n';
import dayjs, { type Dayjs } from 'dayjs';
import { z } from 'zod';
import { PAGE_SIZE_IN_BACKEND } from './constants';
import { addPrefixToStringValues } from './string-array-transformer';
import { stringToBoolean } from './string-bool-translator';

export const fiscalYearsType = z.number().int().array();

export const sortOrderType = z.number().int().min(1);

const amountUnitType = z.enum(['plain', 'thousand', 'million']);

export type AmountUnit = z.infer<typeof amountUnitType>;

export const AmountUnitMultipliers = {
  plain: 1,
  thousand: 1_000,
  million: 1_000_000,
} as const satisfies Record<AmountUnit, number>;

export const AmountUnitLabelModifier = {
  plain: t('yen-unit'),
  thousand: t('thousand-yen-unit'),
  million: t('million-yen-unit'),
} as const satisfies Record<AmountUnit, string>;

export const booleanStringType = z.enum(['true', 'false']);

export const booleanWithBooleanStringType = z.union([
  z.boolean(),
  booleanStringType.transform(stringToBoolean),
]);

export const DayjsSchema = (message?: string) =>
  z.instanceof(dayjs as unknown as typeof Dayjs, {
    message: message ?? t('error-message-invalid-dayjs-data'),
  });

export const DateRangeSchema = z.tuple([
  DayjsSchema().optional().catch(undefined),
  DayjsSchema().optional().catch(undefined),
]);

const literalType = z.union([z.string(), z.number(), z.boolean(), z.null()]);
type Literal = z.infer<typeof literalType>;
type Json = Literal | { [key: string]: Json } | Json[];
export const jsonType: z.ZodType<Json> = z.lazy(() =>
  z.union([literalType, z.array(jsonType), z.record(jsonType)]),
);

export const BackendBaseModel = z.object({
  id: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const BulkUpdateStatusBaseModel = z.object({
  result: z.string(),
  targetCount: z.number(),
  skippedIds: z.string().uuid().array(),
});

const NestedBaseModel = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  level: z.number().int(),
});

type NestedBaseModelType = z.infer<typeof NestedBaseModel> & {
  descendant: NestedBaseModelType[];
};

const NestedBaseDataModel: z.ZodType<NestedBaseModelType[]> =
  NestedBaseModel.extend({
    descendant: z.lazy(() => NestedBaseDataModel),
  }).array();

const OptionModel = z.object({ label: z.string(), value: z.string().uuid() });
export const OptionsModel = OptionModel.array();

type RecursiveOption = z.infer<typeof OptionModel> & {
  children: RecursiveOption[];
};
const NestedOptionElement: z.ZodType<RecursiveOption> = OptionModel.extend({
  children: z.lazy(() => NestedOptionElement.array()),
});

export const NestedOptionModel = NestedOptionElement.array();

// Reference of password policy (written in Japanese)
// https://www.soumu.go.jp/main_sosiki/cybersecurity/kokumin/business/business_staff_01.html
// https://security-portal.nisc.go.jp/guidance/pdf/handbook/handbook-06.pdf
export const passwordType = z
  .string()
  .min(8)
  .regex(
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[*+.\\()?[\]^$\-|!#%&"',/:;<=>@_`{}~]).{8,64}$/,
    {
      message: t('validation.password-not-match-criteria'),
    },
  )
  .regex(/^[a-zA-Z0-9*+.\\()?[\]^$\-|!#%&"',/:;<=>@_`{}~]+$/, {
    message: t('validation.password-include-invalid-character'),
  });

// To show a "required(必須です。)" validation message for a blank('') inputs
// Ref: https://github.com/colinhacks/zod/issues/2466
export const requiredStringType = z
  .string()
  .trim()
  .min(1, { message: t('validation.required') });

export const numericStringType = z.string().regex(/^[0-9]+$/, {
  message: t('validation.half-width-numeric-required'),
});

export const alphabeticStringType = z.string().regex(/^[a-zA-Z]+$/, {
  message: t('validation.half-width-alphabetic-required'),
});

export const alphanumericStringType = z.string().regex(/^[a-zA-Z0-9]+$/, {
  message: t('validation.half-width-alphanumeric-required'),
});

export const paginationSearchParamSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().default(PAGE_SIZE_IN_BACKEND),
});

export const extendEnumWithPrefix = <
  T extends [string, ...string[]],
  P extends string,
>(
  schema: z.ZodEnum<T>,
  prefix: P,
) => {
  const prefixedOptions = addPrefixToStringValues(schema.options, prefix);

  return z.enum([...schema.options, ...prefixedOptions] as const);
};

export const parseCommaSeparatedStringToArray = <T extends z.ZodTypeAny>(
  schema: T,
) => {
  return z
    .string()
    .transform((v) => v.split(','))
    .pipe(schema.array());
};
